import ContentLayout from '@/components/layout/ContentLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useGetCurrentTask, submitAnnotation } from '@/services/generatedAPIs';


type QuestionCandidate = {
  id: number;
  strategy: string;
  grade: string;
  difficulty: string;
  questionType: string;
  subject: string;
  unit: string;
  objective: string;
  topic: string;
  questionText: string;
  questionAns: string;
};

type ExpertTask = {
  taskId: number;
  weekNo: number;
  leftCandidate: QuestionCandidate;
  rightCandidate: QuestionCandidate;
  currentIndex: number;
  totalTasks: number;
};

export default function ExpertAnnotationPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'left' | 'right' | null>(null);
  const [task, setTask] = useState<ExpertTask | null>(null);
  const [reason, setReason] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalTasks, setTotalTasks] = useState(36);

  // 在组件顶层调用 Hook
  const { data: taskResponse, error, mutate } = useGetCurrentTask();

  // 使用 useEffect 监听数据变化
  useEffect(() => {
    if (taskResponse?.code === 200 && taskResponse.data) {
      const taskData = taskResponse.data;
      setTask(taskData as ExpertTask);
      setCurrentIndex(taskData.currentIndex || 0);
      setTotalTasks(36);
      setSelectedOption(null);
      setReason('');
    } else if (taskResponse && taskResponse.code !== 200) {
      // 没有任务时，设置 currentIndex 为 totalTasks，触发完成显示
      setCurrentIndex(totalTasks);
      setTask(null);
    }
  }, [taskResponse, totalTasks]);

  useEffect(() => {
    if (error) {
      console.error('加载题目失败', error);
      toast.error('加载题目失败');
    }
  }, [error]);

  const handleSelect = (choice: 'left' | 'right') => {
    setSelectedOption(choice);
  };

  const handleSubmit = async () => {
    if (!selectedOption) {
      toast.error('请先选择一个题目');
      return;
    }
    if (!reason.trim()) {
      toast.error('请填写选择理由');
      return;
    }

    try {
      setIsLoading(true);
      const data = await submitAnnotation({
        taskId: task?.taskId,
        selectedCandidateId: selectedOption === 'left' ? task?.leftCandidate.id : task?.rightCandidate.id,
        reason: reason
      });

      if (data.code === 200) {
        toast.success('提交成功');
        mutate();
      } else {
        toast.error('提交失败');
      }
    } catch (error) {
      console.error('提交失败', error);
      toast.error('提交失败');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContentLayout title="专家标注">
      <div className="flex flex-col bg-background p-12">
        <div className="flex flex-col w-full bg-muted place-self-center rounded-2xl border-2 border-black-300 gap-6 pb-8 px-8 pt-4">

          {/* 进度显示 */}
          <div className="text-center pt-4">
            <span className="text-lg font-medium">
              题目进度: {currentIndex } / {totalTasks}
            </span>
          </div>

          {/* 完成提示 */}
          {currentIndex >= totalTasks ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="text-6xl">🎉</div>
              <h2 className="text-2xl font-bold text-primary">恭喜完成本周标注！</h2>
              <p className="text-gray-600">您已完成本周全部 {totalTasks} 道题目的标注</p>
            </div>
          ) : (
            <>
              {/* 题目基本信息 */}
              {task && (
                <div className="bg-white rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div><span className="font-medium">年级:</span> {task.leftCandidate.grade}</div>
                    <div><span className="font-medium">科目:</span> {task.leftCandidate.subject}</div>
                    <div><span className="font-medium">单元:</span> {task.leftCandidate.unit}</div>
                    <div><span className="font-medium">题型:</span> {task.leftCandidate.questionType}</div>
                    <div><span className="font-medium">难度:</span> {task.leftCandidate.difficulty}</div>
                    <div><span className="font-medium">知识点:</span> {task.leftCandidate.objective}</div>
                  </div>
                </div>
              )}

              {/* 双题对比界面 */}
              <div className="grid grid-cols-2 gap-6">
            {/* 左侧题目 */}
            <div className={`border-2 rounded-lg p-6 space-y-4 bg-gray-50/50 shadow-md hover:shadow-lg transition-shadow hover:scale-[1.01] transition-transform ${
              selectedOption === 'left' ? 'border-primary' : 'border-gray-200'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-primary text-white text-s font-bold px-3 py-1 rounded-full">A</span>
                <span className="text-sm font-medium text-gray-700">题目</span>
              </div>

              <div className="space-y-3">
                <div className="border rounded-md p-4 bg-white min-h-[120px] text-left shadow-sm whitespace-pre-wrap break-words">
                  {task?.leftCandidate.questionText || '暂无题目'}
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-sm font-medium text-left text-gray-600">答案</div>
                <div className="border rounded-md p-4 bg-white min-h-[80px] text-left shadow-sm whitespace-pre-wrap break-words">
                  {task?.leftCandidate.questionAns || '暂无答案'}
                </div>
              </div>

              <Button
                className="w-full bg-primary"
                onClick={() => handleSelect('left')}
                disabled={isLoading}
              >
                选择 A
              </Button>
            </div>

            {/* 右侧题目 */}
            <div className={`border-2 rounded-lg p-6 space-y-4 bg-gray-50/50 shadow-md hover:shadow-lg transition-shadow hover:scale-[1.01] transition-transform ${
              selectedOption === 'right' ? 'border-primary' : 'border-gray-200'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-primary text-white text-s font-bold px-3 py-1 rounded-full">B</span>
                <span className="text-sm font-medium text-gray-700">题目</span>
              </div>

              <div className="space-y-3">
                <div className="border rounded-md p-4 bg-white min-h-[120px] text-left shadow-sm whitespace-pre-wrap break-words">
                  {task?.rightCandidate.questionText || '暂无题目'}
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-sm font-medium text-left text-gray-600">答案</div>
                <div className="border rounded-md p-4 bg-white min-h-[80px] text-left shadow-sm whitespace-pre-wrap break-words">
                  {task?.rightCandidate.questionAns || '暂无答案'}
                </div>
              </div>

              <Button
                className="w-full bg-primary"
                onClick={() => handleSelect('right')}
                disabled={isLoading}
              >
                选择 B
              </Button>
            </div>
          </div>

          {/* 选择理由 */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-base font-medium">
              选择理由 {selectedOption && `(已选择: ${selectedOption === 'left' ? 'A' : 'B'})`}
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="请说明您选择该题目的理由，可从以下方面考虑：题目清晰度；知识点匹配度; 答案正确性"
              className="min-h-[120px]"
              disabled={isLoading}
            />
          </div>

          {/* 提交按钮 */}
          <div className="flex justify-center gap-4">
            <Button
              onClick={handleSubmit}
              disabled={isLoading || !selectedOption}
              className="bg-primary px-8"
            >
              {isLoading ? '提交中...' : '提交标注'}
            </Button>
          </div>
          </>
          )}
        </div>
      </div>
    </ContentLayout>
  );
}
